import omit from "lodash/omit";

import { buildCustomFieldDefaults } from "components/BaseForm";
import { toFormInvoiceLine } from "routes/revenue_opportunity/form/invoiceLines";

export const FORM_MODES = {
  create: "create",
  edit: "edit",
  clone: "clone",
};

const EMPTY_VALUES = {
  entity_id: null,
  campaign_id: null,
  contact_id: null,
  case_ids: [],
  status_id: null,
  effective_date: null,
  region_id: null,
  country: null,
  assignee_id: null,
  recipient_type: null,
  recipient_id: null,
  billing_to: "",
  purchase_order: [],
  invoice: [],
  invoice_date: null,
  sales_order: [],
  sales_order_date: null,
  launch_date: null,
  actionable_machines: "",
  comments: "",
  invoices: [],
  points_of_contact: [],
  tags: [],
  attachments: [],
};

const fromRecord = record => ({
  ...EMPTY_VALUES,
  entity_id: record.entity_id ?? null,
  campaign_id: record.campaign_id ?? null,
  contact_id: record.contact_id ?? null,
  case_ids: record.case_ids ?? [],
  status_id: record.status_id ?? null,
  effective_date: record.effective_date ?? null,
  region_id: record.region_id ?? null,
  country: record.country ?? null,
  assignee_id: record.assignee_id ?? null,
  recipient_type: record.recipient_type ?? null,
  recipient_id: record.recipient_id ?? null,
  billing_to: record.billing_to ?? "",
  purchase_order: record.purchase_order ?? [],
  invoice: record.invoice ?? [],
  invoice_date: record.invoice_date ?? null,
  sales_order: record.sales_order ?? [],
  sales_order_date: record.sales_order_date ?? null,
  launch_date: record.launch_date ?? null,
  actionable_machines:
    record.actionable_machines == null
      ? ""
      : String(record.actionable_machines),
  comments: record.comments ?? "",
  invoices: (record.invoices ?? []).map(toFormInvoiceLine),
  points_of_contact: (record.points_of_contact ?? []).map(poc => ({
    point_of_contact_id: poc.point_of_contact_id,
    is_launch_contact: Boolean(poc.is_launch_contact),
    is_reporting_contact: Boolean(poc.is_reporting_contact),
  })),
  tags: record.tags ?? [],
  attachments: record.attachments ?? [],
});

const fromPreset = (preset, entityPrefill) => ({
  ...EMPTY_VALUES,
  entity_id: preset.entityId ?? null,
  campaign_id: preset.campaignId ?? null,
  contact_id: preset.contactId ?? null,
  case_ids: preset.caseId ? [preset.caseId] : [],
  region_id: entityPrefill?.region_id ?? null,
  country: entityPrefill?.country ?? null,
});

// A country no longer configured for its region is cleared so the user
// re-picks it. `regionCountries` is null while the region is unresolved.
const withoutStaleCountry = (values, regionCountries) =>
  regionCountries && values.country && !regionCountries.includes(values.country)
    ? { ...values, country: null }
    : values;

/**
 * Initial form values for each mode.
 *
 * A clone drops every record and invoice line id so the API creates new rows,
 * and keeps the source attachments in `attachments` so they are copied at
 * submit. `cloneTags` replaces the source tags, because a non-Admin may only
 * carry the tags they could apply by hand.
 */
export const buildDefaultValues = ({
  mode,
  record,
  preset = {},
  entityPrefill,
  regionCountries = null,
  customFieldConfigs,
  cloneTags,
}) => {
  if (mode === FORM_MODES.create) {
    return withoutStaleCountry(
      {
        ...fromPreset(preset, entityPrefill),
        custom_fields: buildCustomFieldDefaults(customFieldConfigs, null),
      },
      regionCountries
    );
  }

  const values = {
    ...fromRecord(record),
    custom_fields: buildCustomFieldDefaults(
      customFieldConfigs,
      record.custom_fields
    ),
  };

  if (mode === FORM_MODES.clone) {
    values.invoices = values.invoices.map(line => omit(line, "id"));
    values.tags = cloneTags ?? values.tags;
  }

  return withoutStaleCountry(values, regionCountries);
};
